import { useState } from 'react';
import { Link } from 'react-router-dom';
import HoitoLogo from '@/components/HoitoLogo';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';

interface PublicLayoutProps {
  children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 lg:h-18">
            <HoitoLogo showText={true} href="/" />
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="/#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                How it works
              </a>
              <a href="/#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Features
              </a>
              <Link to="/tools" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Tools
              </Link>
              <Link to="/login" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Sign in
              </Link>
              <Button asChild size="lg" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold shrink-0 btn-hover-lift">
                <Link to="/register">Get started free</Link>
              </Button>
            </div>
            {/* Mobile nav */}
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden -mr-2">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-1 pt-2">
                  <a href="/#how-it-works" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    How it works
                  </a>
                  <a href="/#features" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Features
                  </a>
                  <Link to="/tools" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Tools
                  </Link>
                  <Link to="/login" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Sign in
                  </Link>
                  <Button asChild className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold btn-hover-lift">
                    <Link to="/register" onClick={() => setNavOpen(false)}>Get started free</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {children}

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10 lg:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
            <div>
              <HoitoLogo href="/" showText={true} />
              <p className="mt-4 text-sm text-slate-600">
                Your personal wealth management companion.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Product</h4>
              <ul className="mt-4 space-y-3">
                <li><a href="/#features" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Features</a></li>
                <li><Link to="/tools" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Tools</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Company</h4>
              <ul className="mt-4 space-y-3">
                <li><Link to="/about" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">About</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Legal</h4>
              <ul className="mt-4 space-y-3">
                <li><Link to="/privacy" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} Hoito. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
